import { useMemo, useState } from 'react';
import { useAppState } from '../state/AppState';
import { evaluateRecord } from '../domain/completeness';
import { RiskBadge } from './RiskBadge';
import { FindingBadge } from './FindingBadge';
import styles from './AuditList.module.css';
import type { RecordFindings, RiskTier } from '../domain/types';

type FilterTier = 'ALL' | RiskTier;
type FilterFinding = 'ALL' | 'DISCREPANCY' | 'INCOMPLETE' | 'CLEAN';

export function AuditList() {
  const { state, setView } = useAppState();
  const [query, setQuery] = useState('');
  const [tierFilter, setTierFilter] = useState<FilterTier>('ALL');
  const [findingFilter, setFindingFilter] = useState<FilterFinding>('ALL');

  const rules = state.rules;

  const enriched = useMemo(() => {
    if (!rules) return [];
    return state.clients.map((c) => ({ record: c, findings: evaluateRecord(c, rules) }));
  }, [state.clients, rules]);

  const stats = useMemo(() => {
    let high = 0,
      medium = 0,
      low = 0,
      discrepancies = 0,
      incomplete = 0;
    for (const { findings } of enriched) {
      if (findings.computed.tier === 'HIGH') high++;
      else if (findings.computed.tier === 'MEDIUM') medium++;
      else low++;
      if (findings.discrepancy) discrepancies++;
      if (findings.incomplete.length > 0) incomplete++;
    }
    return { total: enriched.length, high, medium, low, discrepancies, incomplete };
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return enriched.filter(({ record, findings }) => {
      if (q) {
        const hay =
          `${record.client_id} ${record.client_name} ${record.branch} ${record.relationship_manager}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (tierFilter !== 'ALL' && findings.computed.tier !== tierFilter) return false;
      if (findingFilter === 'DISCREPANCY' && !findings.discrepancy) return false;
      if (findingFilter === 'INCOMPLETE' && findings.incomplete.length === 0) return false;
      if (
        findingFilter === 'CLEAN' &&
        (findings.discrepancy || findings.incomplete.length > 0)
      ) {
        return false;
      }
      return true;
    });
  }, [enriched, query, tierFilter, findingFilter]);

  return (
    <section className={styles.wrap}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Audit list</h1>
          <p className={styles.subtitle}>
            Computed against rules v{rules?.version ?? '—'}. Click any row to inspect the
            record.
          </p>
        </div>
      </header>

      <div className={styles.kpis} role="group" aria-label="Portfolio statistics">
        <Kpi label="Total records" value={stats.total} />
        <Kpi label="HIGH" value={stats.high} tone="error" />
        <Kpi label="MEDIUM" value={stats.medium} tone="warning" />
        <Kpi label="LOW" value={stats.low} tone="success" />
        <Kpi label="Discrepancies" value={stats.discrepancies} tone="error" />
        <Kpi label="Incomplete" value={stats.incomplete} tone="warning" />
      </div>

      {state.csvIngestErrors.length > 0 && (
        <div className={styles.ingestError} role="alert">
          <strong>{state.csvIngestErrors.length} CSV rows were rejected at ingest.</strong>{' '}
          These records did not pass schema validation and are not loaded.
        </div>
      )}

      <div className={styles.filters} role="group" aria-label="Filters">
        <label className={styles.filterField}>
          <span className="sr-only">Search</span>
          <input
            className={styles.search}
            type="search"
            placeholder="Search client, ID, RM, branch…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            maxLength={120}
          />
        </label>
        <label className={styles.filterField}>
          <span className={styles.filterLabel}>Risk tier</span>
          <select
            className={styles.select}
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value as FilterTier)}
          >
            <option value="ALL">All</option>
            <option value="HIGH">HIGH</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="LOW">LOW</option>
          </select>
        </label>
        <label className={styles.filterField}>
          <span className={styles.filterLabel}>Findings</span>
          <select
            className={styles.select}
            value={findingFilter}
            onChange={(e) => setFindingFilter(e.target.value as FilterFinding)}
          >
            <option value="ALL">All</option>
            <option value="DISCREPANCY">Discrepancies</option>
            <option value="INCOMPLETE">Incomplete</option>
            <option value="CLEAN">Clean</option>
          </select>
        </label>
      </div>

      <div className={styles.tableWrap} role="region" aria-label="Client records">
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Client</th>
              <th scope="col">Branch</th>
              <th scope="col">Date</th>
              <th scope="col">Country</th>
              <th scope="col">Computed</th>
              <th scope="col">Stored</th>
              <th scope="col">Findings</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  No records match the current filters.
                </td>
              </tr>
            )}
            {filtered.map(({ record, findings }) => (
              <Row
                key={record.client_id}
                clientId={record.client_id}
                name={record.client_name}
                branch={record.branch}
                date={record.onboarding_date}
                country={record.country_of_tax_residence}
                findings={findings}
                onOpen={() => setView({ kind: 'detail', clientId: record.client_id })}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'warning' | 'error';
}) {
  return (
    <div className={`${styles.kpi} ${tone ? styles[`kpi_${tone}`] : ''}`}>
      <div className={styles.kpiValue}>{value}</div>
      <div className={styles.kpiLabel}>{label}</div>
    </div>
  );
}

function Row({
  clientId,
  name,
  branch,
  date,
  country,
  findings,
  onOpen,
}: {
  clientId: string;
  name: string;
  branch: string;
  date: string;
  country: string;
  findings: RecordFindings;
  onOpen: () => void;
}) {
  return (
    <tr className={styles.row}>
      <td>
        <button type="button" className={styles.rowLink} onClick={onOpen}>
          <div className={styles.clientName}>{name}</div>
          <div className={styles.clientId}>{clientId}</div>
        </button>
      </td>
      <td>{branch}</td>
      <td>{date}</td>
      <td>{country}</td>
      <td>
        <RiskBadge tier={findings.computed.tier} size="sm" />
      </td>
      <td>
        {findings.storedTier ? (
          <RiskBadge tier={findings.storedTier} size="sm" />
        ) : (
          <span className={styles.muted}>—</span>
        )}
      </td>
      <td className={styles.findingsCell}>
        <div className={styles.findingsCellInner}>
          {findings.discrepancy && (
            <FindingBadge
              kind="discrepancy"
              title={`Stored ${findings.storedTier || 'none'} vs computed ${findings.computed.tier}`}
            >
              Discrepancy
            </FindingBadge>
          )}
          {findings.incomplete.length > 0 && (
            <FindingBadge
              kind="incomplete"
              title={findings.incomplete.map((i) => i.message).join(' · ')}
            >
              {findings.incomplete.length} incomplete
            </FindingBadge>
          )}
          {!findings.discrepancy && findings.incomplete.length === 0 && (
            <span className={styles.muted}>Clean</span>
          )}
        </div>
      </td>
    </tr>
  );
}
